<template>
    <div class="agenda-day-cell"
         :data-date="date"
         :class="{
             'agenda-day-cell--today': isToday,
             'agenda-day-cell--selected': isSelected,
             'agenda-day-cell--other-month': !isCurrentMonth,
             'agenda-day-cell--drag-over': isDragOver
         }"
         :style="multidayOffset > 0 ? {'--multiday-offset': multidayOffset} : null"
         @click="$emit('click', date)"
         @dblclick.stop="$emit('dblclick', date)"
         @dragover.prevent="onDragOver"
         @dragleave="isDragOver = false"
         @drop="onDrop">
        <div class="agenda-day-cell-header">
            <span class="agenda-day-number" :class="{'agenda-day-number--today': isToday}">{{ dayNumber }}</span>
        </div>
        <div class="agenda-day-cell-events">
            <div v-for="(evt, i) in visibleEvents" :key="evt.id"
                 class="agenda-day-event"
                 :class="{'agenda-day-event--completed': evt.status?.id === 2}"
                 :style="{background: evt.color || '#007AFF', color: getTextColor(evt.color || '#007AFF')}"
                 :data-evt-id="evt.id"
                 :draggable="evt.user?.id === currentUserId"
                 @dragstart.stop="onEventDragStart($event, evt)"
                 @click.stop="onEventClick(evt)"
                 @contextmenu.prevent.stop="$emit('eventContextMenu', $event, evt)"
                 @mouseenter="$emit('eventHover', {evt, event: $event})"
                 @mouseleave="$emit('eventLeave')"
                 @touchstart.passive="onLongPressStart($event, evt)"
                 @touchend.passive="onLongPressEnd($event)"
                 @touchmove.passive="onLongPressMove($event)">
                <span v-if="evt.user?.id === currentUserId" class="agenda-day-event-radio" :class="{'agenda-day-event-radio--done': evt.status?.id === 2}" @click.stop="$emit('statusToggle', {id: evt.id, status_id: evt.status?.id === 2 ? 1 : 2})"></span>
                <span class="agenda-day-event-time" v-if="getTime(evt)">{{ getTime(evt) }}</span>
                <svg v-if="evt.recurrence" class="agenda-day-event-recurrence" width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                <span class="agenda-day-event-title">{{ evt.title }}</span>
            </div>
            <div v-if="extraCount > 0" class="agenda-day-more" @click.stop="$emit('click', date)">
                +{{ extraCount }} <span class="agenda-day-more-text">{{ $t('pages.module.AGENDA.panel.more') }}</span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'AgendaDayCell',
    emits: ['click', 'dblclick', 'eventDrop', 'eventClick', 'eventContextMenu', 'statusToggle', 'eventHover', 'eventLeave'],
    props: {
        date: {type: String, required: true},
        events: {type: Array, default: () => []},
        isToday: {type: Boolean, default: false},
        isCurrentMonth: {type: Boolean, default: true},
        isSelected: {type: Boolean, default: false},
        currentUserId: {type: [Number, String], default: null},
        maxVisible: {type: Number, default: 3},
        multidayOffset: {type: Number, default: 0},
    },
    data() {
        return {
            isDragOver: false,
        }
    },
    computed: {
        dayNumber() {
            return parseInt(this.date.split('-')[2], 10)
        },
        visibleEvents() {
            return this.events.slice(0, this.maxVisible)
        },
        extraCount() {
            return Math.max(0, this.events.length - this.maxVisible)
        },
    },
    methods: {
        getTime(evt) {
            if (!evt.event_at) return ''
            const start = evt.event_at.substring(11, 16)
            if (evt.event_end_at && evt.event_end_at.substring(0, 10) === evt.event_at.substring(0, 10)) {
                return start + '-' + evt.event_end_at.substring(11, 16)
            }
            return start
        },
        getTextColor(bgColor) {
            if (!bgColor) return '#fff'
            const hex = bgColor.replace('#', '')
            const r = parseInt(hex.substring(0, 2), 16)
            const g = parseInt(hex.substring(2, 4), 16)
            const b = parseInt(hex.substring(4, 6), 16)
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
            return luminance > 0.6 ? '#1c1c1e' : '#ffffff'
        },
        onDragOver(e) {
            this.isDragOver = true
        },
        onDrop(e) {
            this.isDragOver = false
            const eventId = e.dataTransfer.getData('text/agenda-event-id')
            if (eventId) {
                this.$emit('eventDrop', {eventId: parseInt(eventId, 10), newDate: this.date})
            }
        },
        onEventDragStart(e, evt) {
            e.dataTransfer.setData('text/agenda-event-id', String(evt.id))
            e.dataTransfer.effectAllowed = 'move'
            const ghost = document.createElement('div')
            ghost.textContent = evt.title
            ghost.style.cssText = `position:fixed; left:-9999px; top:-9999px; padding:3px 8px; background:${evt.color || '#007AFF'}; color:${this.getTextColor(evt.color || '#007AFF')}; border-radius:4px; font-size:11px; font-weight:500; max-width:150px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;`
            document.body.appendChild(ghost)
            e.dataTransfer.setDragImage(ghost, ghost.offsetWidth / 2, ghost.offsetHeight + 10)
            requestAnimationFrame(() => ghost.remove())
        },
        onEventClick(evt) {
            if (this._longPressed) {
                this._longPressed = false
                return
            }
            this.$emit('eventClick', evt)
        },
        onLongPressStart(e, evt) {
            const touch = e.touches[0]
            this._lpStartX = touch.clientX
            this._lpStartY = touch.clientY
            this._longPressed = false
            this._longPressReady = false
            this._lpEvent = evt
            this._longPressTimer = setTimeout(() => {
                this._longPressReady = true
                this._longPressTimer = null
            }, 600)
        },
        onLongPressMove(e) {
            if (!this._longPressTimer && !this._longPressReady) return
            const touch = e.touches[0]
            const dx = Math.abs(touch.clientX - this._lpStartX)
            const dy = Math.abs(touch.clientY - this._lpStartY)
            if (dx > 20 || dy > 20) {
                clearTimeout(this._longPressTimer)
                this._longPressTimer = null
                this._longPressReady = false
            }
        },
        onLongPressEnd(e) {
            clearTimeout(this._longPressTimer)
            this._longPressTimer = null
            if (this._longPressReady) {
                const touch = e.changedTouches[0]
                const dx = Math.abs(touch.clientX - this._lpStartX)
                const dy = Math.abs(touch.clientY - this._lpStartY)
                if (dx < 40 && dy < 40) {
                    this._longPressReady = false
                    this._longPressed = true
                    this.$emit('eventContextMenu', {clientX: this._lpStartX, clientY: this._lpStartY, preventDefault() {}}, this._lpEvent)
                    return
                }
            }
            this._longPressReady = false
        },
    },
}
</script>
